import React from 'react';

// ==========================================
// CONFIGURATION: Set to false to disable the flag globally
// ==========================================
export const ENABLE_INDEPENDENCE_DAY = true;

interface IndependenceDayFlagProps {
  isMenuOpen?: boolean;
}

const IndependenceDayFlag: React.FC<IndependenceDayFlagProps> = ({ isMenuOpen = true }) => {
  if (!ENABLE_INDEPENDENCE_DAY) return null;

  // Generate 24 spokes for the Ashok Chakra
  const spokes = Array.from({ length: 24 }).map((_, i) => {
    const angle = (i * 360) / 24;
    const angleRad = (angle * Math.PI) / 180;
    return (
      <line
        key={i}
        x1="150"
        y1="100"
        x2={150 + 23 * Math.cos(angleRad)}
        y2={100 + 23 * Math.sin(angleRad)}
        stroke="#000080"
        strokeWidth="1.2"
      />
    );
  });

  return (
    <div className={`tiranga-wrapper ${isMenuOpen ? 'menu-open' : 'menu-closed'}`}>
      {/* Dynamic, 100% GPU-Accelerated CSS Styles & Keyframes */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .tiranga-wrapper {
          position: absolute;
          pointer-events: none;
          z-index: 999;
          display: flex;
          align-items: flex-start;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          perspective: 1200px;
        }

        /* Large (Sidebar Open) Styling */
        .tiranga-wrapper.menu-open {
          left: 45px;
          top: 12px;
          width: 90px;
          height: 110px;
        }

        /* Small (Sidebar Closed/Collapsed) Styling */
        .tiranga-wrapper.menu-closed {
          left: 20px;
          top: 10px;
          width: 50px;
          height: 60px;
        }

        /* Sleek 3D Metallic Flagpole */
        .tiranga-pole {
          position: relative;
          background: linear-gradient(to right, #cfcfcf 0%, #ffffff 30%, #9e9e9e 70%, #4a4a4a 100%);
          box-shadow: 1px 1px 3px rgba(0,0,0,0.18);
          border-radius: 2px;
          transform: rotate(-6deg);
          transform-origin: bottom center;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }

        .menu-open .tiranga-pole {
          width: 4px;
          height: 104px;
        }

        .menu-closed .tiranga-pole {
          width: 2.5px;
          height: 54px;
        }

        /* Golden Ball (Finial) at top of the pole */
        .tiranga-pole::before {
          content: '';
          position: absolute;
          border-radius: 50%;
          background: radial-gradient(circle at 30% 30%, #fff4b8 0%, #ffdb1a 45%, #b58404 80%, #523c02 100%);
          box-shadow: 0.5px 0.5px 2px rgba(0,0,0,0.25);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .menu-open .tiranga-pole::before {
          width: 8px;
          height: 8px;
          top: -6px;
          left: -2px;
        }

        .menu-closed .tiranga-pole::before {
          width: 5px;
          height: 5px;
          top: -4px;
          left: -1.2px;
        }

        /* Waving Flag Container with 3D context */
        .tiranga-flag-container {
          position: relative;
          border-radius: 1px;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform-origin: left center;
          
          /* GPU-accelerated shadows & 3D rendering context */
          box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.25);
          transform-style: preserve-3d;
          will-change: transform;
          
          /* Butter-smooth hardware-accelerated wave animation */
          animation: tiranga-wave-3d 2.8s ease-in-out infinite;
        }

        .menu-open .tiranga-flag-container {
          width: 72px;
          height: 48px;
          margin-left: -1px;
          margin-top: 3px;
        }

        .menu-closed .tiranga-flag-container {
          width: 39px;
          height: 26px;
          margin-left: -0.5px;
          margin-top: 1.5px;
        }

        /* Shading overlay simulating cloth folds and moving highlights (GPU-accelerated) */
        .tiranga-shading {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 2;
          pointer-events: none;
          background: linear-gradient(
            115deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.28) 20%,
            rgba(0, 0, 0, 0.16) 40%,
            rgba(255, 255, 255, 0.35) 60%,
            rgba(0, 0, 0, 0.20) 80%,
            rgba(255, 255, 255, 0) 100%
          );
          background-size: 300% 100%;
          mix-blend-mode: overlay;
          will-change: background-position;
          
          /* In sync with the physical wave animation for a realistic 3D appearance */
          animation: tiranga-shading-move 2.8s linear infinite;
        }

        .tiranga-flag-svg {
          display: block;
          width: 100%;
          height: 100%;
          z-index: 1;
        }

        /* 
          100% GPU-Accelerated 3D Waving/Flapping Animation.
          Using combinations of translate3d, rotate3d, skewY to simulate a wind-blown wave.
        */
        @keyframes tiranga-wave-3d {
          0% {
            transform: translate3d(0, 0, 0) rotateX(4deg) rotateY(18deg) rotateZ(-5deg) skewY(-2deg);
          }
          25% {
            transform: translate3d(0, -1.5px, 4px) rotateX(0deg) rotateY(26deg) rotateZ(-5.5deg) skewY(-0.5deg);
          }
          50% {
            transform: translate3d(0, -3px, 0px) rotateX(-4deg) rotateY(18deg) rotateZ(-6deg) skewY(2deg);
          }
          75% {
            transform: translate3d(0, -1.5px, -4px) rotateX(0deg) rotateY(10deg) rotateZ(-5.5deg) skewY(-0.5deg);
          }
          100% {
            transform: translate3d(0, 0, 0) rotateX(4deg) rotateY(18deg) rotateZ(-5deg) skewY(-2deg);
          }
        }

        /* Moving Highlights/Shading Animation (matching the wave period) */
        @keyframes tiranga-shading-move {
          0% {
            background-position: 0% 50%;
          }
          100% {
            background-position: -300% 50%;
          }
        }
      `,
        }}
      />

      {/* Flagpole */}
      <div className="tiranga-pole" />

      {/* Flag with SVG & shading overlay */}
      <div className="tiranga-flag-container">
        <div className="tiranga-shading" />
        <svg className="tiranga-flag-svg" viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
          {/* Saffron Stripe */}
          <rect width="300" height="66.67" fill="#FF9933" />
          {/* White Stripe */}
          <rect y="66.67" width="300" height="66.67" fill="#FFFFFF" />
          {/* Green Stripe */}
          <rect y="133.33" width="300" height="66.67" fill="#128807" />

          {/* Ashok Chakra */}
          <g>
            {/* Circular Ring */}
            <circle cx="150" cy="100" r="23" fill="none" stroke="#000080" strokeWidth="1.8" />
            {/* Center Hub */}
            <circle cx="150" cy="100" r="4.5" fill="#000080" />
            {/* 24 Spokes */}
            {spokes}
          </g>
        </svg>
      </div>
    </div>
  );
};

export default IndependenceDayFlag;
